import { Colors, ColorName, IconName } from '@zapier/design-system';
import {
  TaskHeldReason,
  TaskStatus,
} from '@zapier/design-system-reporting/src/graphql-types';
import {
  StatusFilterType,
  ActionFilterType,
} from '@zapier/design-system-reporting/src/types';

export const unnamedZapTitle = 'Name your zap';

export const routes = {
  log: '/app/history',
  usage: '/app/history/usage',
};

export type ActionFilter = {
  label: string;
  value: ActionFilterType;
};

export const actionFilters: ActionFilter[] = [
  {
    label: 'Unpaused',
    value: ActionFilterType.switched_on,
  },
  {
    label: 'Paused',
    value: ActionFilterType.switched_off,
  },
];

export type StatusFilter = {
  label: string;
  status: TaskStatus[];
  value: StatusFilterType;
};

export const statusFilters: StatusFilter[] = [
  {
    label: 'Success',
    status: [TaskStatus.success],
    value: StatusFilterType.success,
  },
  {
    label: 'Filtered',
    status: [TaskStatus.filtered],
    value: StatusFilterType.filtered,
  },
  {
    label: 'Stopped / Errored',
    status: [TaskStatus.error],
    value: StatusFilterType.error,
  },
  {
    label: 'Stopped / Halted',
    status: [TaskStatus.halted],
    value: StatusFilterType.halted,
  },
  {
    label: 'Waiting / Delayed',
    status: [TaskStatus.delayed],
    value: StatusFilterType.delayed,
  },
  {
    label: 'Waiting / Scheduled',
    status: [TaskStatus.scheduled],
    value: StatusFilterType.scheduled,
  },
  {
    label: 'Playing',
    status: [TaskStatus.pending, TaskStatus.replay],
    value: StatusFilterType.playing,
  },
  {
    label: 'Held',
    status: [TaskStatus.held, TaskStatus.throttled],
    value: StatusFilterType.held,
  },
];

const TaskHeldReasonsDescriptions = {
  [TaskHeldReason.access_blacklisted_app]: 'Restricted App',
  [TaskHeldReason.blacklisted_app]: 'Premium App',
  [TaskHeldReason.paymentIssue]: 'Payment issue',
  [TaskHeldReason.stale_auth]: 'Disconnected app',
  [TaskHeldReason.step_limit]: 'Step limit reached',
  [TaskHeldReason.task_limit]: 'Task limit reached',
  [TaskHeldReason.throttled]: 'Too many tasks',
  [TaskHeldReason.too_many]: 'Too much data',
  [TaskHeldReason.unknown]: 'Held',
};

export function convertStatusValueToFilter(
  value: StatusFilterType[] | undefined
) {
  // If we have no statuses or all statuses selected do not filter
  if (
    !value ||
    value.length === 0 ||
    value.length === Object.keys(StatusFilterType).length
  ) {
    return undefined;
  }

  return (
    statusFilters
      .filter(f => value.includes(f.value))
      // @ts-ignore
      .reduce((acc, f) => [...acc, ...f.status], [])
  );
}

export const statusMap: {
  [key in TaskStatus]: {
    color: string;
    icon: IconName;
    iconColor?: ColorName;
    iconSize?: number;
    label: string;
    subLabel?: string;
  };
} = {
  delayed: {
    color: Colors.blue500,
    icon: 'miscClock',
    label: 'Waiting',
    subLabel: 'Delayed',
  },
  delete: {
    color: Colors.grey500,
    icon: 'actionTrash',
    label: 'Deleting',
  },
  deleted: {
    color: Colors.grey500,
    icon: 'actionTrash',
    label: 'Deleted',
  },
  error: {
    color: Colors.red600,
    icon: 'miscHand',
    label: 'Stopped',
    subLabel: 'Errored',
  },
  filtered: {
    color: Colors.grey500,
    icon: 'statusFilter',
    iconSize: 2,
    label: 'Filtered',
  },
  halted: {
    color: Colors.red600,
    icon: 'miscHand',
    label: 'Stopped',
    subLabel: 'Halted',
  },
  held: {
    color: Colors.yellow500,
    icon: 'statusPaused',
    label: 'Holding',
  },
  pending: {
    color: '#fff3ec',
    icon: 'statusPlaying',
    iconColor: 'orange500',
    label: 'Playing',
  },
  replay: {
    color: '#fff3ec',
    icon: 'statusPlaying',
    iconColor: 'orange500',
    label: 'Playing',
  },
  scheduled: {
    color: Colors.blue500,
    icon: 'miscClock',
    label: 'Waiting',
    subLabel: 'Scheduled',
  },
  skipped: {
    color: Colors.grey500,
    icon: 'alertInfo',
    label: 'Skipped',
  },
  success: {
    color: Colors.green400,
    icon: 'formCheck',
    label: 'Success',
  },
  throttled: {
    color: Colors.yellow500,
    icon: 'statusPaused',
    label: 'Holding',
    subLabel: 'Throttled',
  },
  unknown: {
    color: Colors.grey500,
    icon: 'alertInfo',
    label: 'Unknown',
  },
};

export function getStatusValues(
  status: TaskStatus,
  heldReason: TaskHeldReason | null,
  heldStaleAuth: string | null
) {
  const values = statusMap[status];

  if (status === TaskStatus.held) {
    if (heldReason === TaskHeldReason.stale_auth && heldStaleAuth) {
      return {
        ...values,
        subLabel: `Reconnect ${heldStaleAuth}`,
      };
    }
    return {
      ...values,
      // @ts-ignore doesn't like the null lookup but it will fall back
      subLabel: TaskHeldReasonsDescriptions[heldReason] || null,
    };
  }

  return values;
}

export const statusColor: { [key in TaskStatus | 'billable']: string } = {
  billable: Colors.blue500,
  delayed: Colors.blue500,
  delete: Colors.pink200,
  deleted: Colors.pink200,
  error: Colors.red600,
  filtered: Colors.grey400,
  halted: Colors.red200,
  held: Colors.yellow600,
  pending: Colors.orange500,
  replay: Colors.orange500,
  scheduled: Colors.aquamarine600,
  skipped: Colors.grey700,
  success: Colors.green400,
  throttled: Colors.yellow400,
  unknown: Colors.purple500,
};
